<script lang="ts" setup>
import { reactive, ref, watch } from 'vue'
import type SwiperClass from 'swiper'
import type { NewsData } from '~/composables/news/interface'
import SidebarCategory from '~/components/mobile/pages/news/sidebar-category.vue'
import SeoHeadings from '~/components/common/seo-headings.vue'
import HotMatch from '~/components/mobile/pages/sports/hot-match.vue'
import NewsCategory from '~/components/mobile/pages/news/news-category.vue'
import { useNews } from '~/composables/news/use-news'
import Promotion from '~/components/common/promotion.vue'
import { sideBarCategories, SLUG_CATEGORY_NEWS } from '~/resources/news'
import { useSwiper } from '~/composables/common/use-swiper'

const { modules, SPACE_BETWEEN_SWIPER, SLIDERS_PER_VIEW } = useSwiper()
const { fetchAllCategoryData, categorySlugCurrent } = useNews()
const dataNews = reactive<NewsData[]>([])
const limit: number = 20
fetchAllCategoryData(dataNews, 0, limit, SLUG_CATEGORY_NEWS.SOI_KEO)

interface SliderState {
  isBlurredRight: boolean
  isBlurredLeft: boolean
}

const sliderState = reactive<SliderState>({
  isBlurredRight: true,
  isBlurredLeft: false
})

function handleSlideChange(swiper: SwiperClass): void {
  const activeIndex = swiper.activeIndex
  const isBeginning = activeIndex === 0
  const isEnd = activeIndex === swiper.pagination.bullets.length - 1

  sliderState.isBlurredRight = isBeginning || !isEnd
  sliderState.isBlurredLeft = !isBeginning || isEnd
}

const categoryName = ref('')
watch(
  () => categorySlugCurrent.value,
  () => {
    const currentCategory = sideBarCategories.find(item => item.slug === categorySlugCurrent.value)
    categoryName.value = currentCategory?.subTitle ?? 'Tin Trong Tuần'
  },
  { immediate: true }
)
</script>
<template>
  <div class="news">
    <div>
      <div class="news__wrapper">
        <SidebarCategory :categorySlugCurrent="categorySlugCurrent" />

        <SeoHeadings />

        <div v-if="categorySlugCurrent === SLUG_CATEGORY_NEWS.ALL && dataNews[0]?.data" class="hot-news">
          <div class="hot-news__content">
            <div class="heading-title">
              <div class="heading-title__left">Tin Hot</div>
            </div>
            <div class="hot-news__slider">
              <Swiper
                ref="newsSwiper"
                loop
                :modules="modules"
                :centeredSlides="true"
                :navigation="{
                  nextEl: '.hot-news__next',
                  prevEl: '.hot-news__prev'
                }"
                :pagination="{ clickable: true }"
                :slidesPerView="1"
                :spaceBetween="SPACE_BETWEEN_SWIPER.LARGE"
                @slideChange="handleSlideChange"
              >
                <SwiperSlide v-for="(item, index) in dataNews[0]?.data.slice(0, 5)" :key="index" class="news-item">
                  <div class="news-item__thumb hover-shine is-vertical">
                    <NuxtLink :to="'/' + item.slug" :title="item.title">
                      <img v-if="item.image" :alt="item.title" :src="item.image" loading="lazy" class="img-hot-news" />
                    </NuxtLink>
                  </div>
                  <div class="news-item__content">
                    <NuxtLink :to="'/' + item.slug" :title="item.title" class="line-limit-1 news-item__title">
                      {{ item.title }}
                    </NuxtLink>
                    <div class="news-item__desc line-limit-2" v-html="item.desc"></div>
                  </div>
                  <div class="news-item__footer">
                    <div class="category-slug">
                      {{ dataNews[0].category.name }}
                    </div>
                    <div class="wrapper-date-time">
                      <span class="icon-clock"></span>
                      <div class="date-time">
                        <div class="time">{{ item.time }}</div>
                        <div class="date">{{ item.date || item.publishTime }}</div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
          </div>
        </div>

        <div id="weekly-news" class="weekly-news">
          <div class="weekly-news__content">
            <div class="heading-title">{{ categoryName }}</div>
            <NewsCategory :categorySlug="categorySlugCurrent" />
          </div>
        </div>
      </div>
    </div>

    <HotMatch :isNews="true" />
    
    <Promotion
      class="news__promotion"
      :slidesPerView="SLIDERS_PER_VIEW.TINY"
      :spaceBetween="SPACE_BETWEEN_SWIPER.LARGE"
    />
  </div>
</template>

<style scoped lang="scss">
@import '~scssAsset/mobile/news/index.scss';
@import '~scssAsset/mobile/news/news-page.scss';
</style>
